import React, { useState, useEffect } from 'react'
import { useAuth } from '../domains/auth/AuthContext'
import { postOrdersApi } from '../services/api'
import { 
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface PostOrder {
  id: string
  title: string
  content: string
  organization: {
    id: string
    name: string
  }
  location: {
    id: string
    name: string
  }
  status: 'PENDING' | 'SIGNED' | 'EXPIRED'
  createdAt: string
  expiresAt: string
  signedAt?: string
  signature?: string
}

export default function PostOrders() {
  const { user } = useAuth()
  const [postOrders, setPostOrders] = useState<PostOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState<PostOrder | null>(null)
  const [signing, setSigning] = useState(false)

  useEffect(() => {
    loadPostOrders()
  }, [])

  const loadPostOrders = async () => {
    try {
      setLoading(true)
      const response = await postOrdersApi.getPostOrders()
      setPostOrders(response.data || [])
    } catch (error) {
      console.error('Error loading post orders:', error)
      toast.error('Failed to load post orders')
    } finally {
      setLoading(false)
    }
  }

  const handleSignOrder = async (orderId: string) => {
    try {
      setSigning(true)
      
      // In a real implementation, this would capture a signature
      // For now, we'll use a placeholder signature URL
      const signatureData = {
        signatureUrl: 'https://example.com/signature-placeholder.jpg'
      }
      
      await postOrdersApi.signPostOrder(orderId, signatureData)
      
      // Refresh the post orders list
      await loadPostOrders()
      
      toast.success('Post order signed successfully')
      setSelectedOrder(null)
    } catch (error) {
      console.error('Error signing post order:', error)
      toast.error('Failed to sign post order')
    } finally {
      setSigning(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />
      case 'SIGNED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'EXPIRED':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pending'
      case 'SIGNED':
        return 'Signed'
      case 'EXPIRED':
        return 'Expired'
      default:
        return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-100'
      case 'SIGNED':
        return 'text-green-600 bg-green-100'
      case 'EXPIRED':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Post Orders</h1>
        <p className="text-text-secondary">Review and sign important documents</p>
      </div>

      {postOrders.length === 0 ? (
        <div className="text-center py-8">
          <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 text-text-muted" />
          <p className="text-text-secondary">No post orders available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {postOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-text-primary mb-1">{order.title}</h3>
                  <p className="text-sm text-text-secondary mb-2">
                    {order.organization.name} • {order.location.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(order.status)}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                    {order.status === 'SIGNED' && (
                      <span className="text-xs text-text-muted">
                        Signed by {order.signature} on {new Date(order.signedAt!).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-text-muted">
                    Expires: {new Date(order.expiresAt).toLocaleDateString()}
                  </p>
                  {isExpired(order.expiresAt) && (
                    <p className="text-xs text-red-500">Expired</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-text-secondary line-clamp-3">{order.content}</p>
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="text-primary-orange text-sm font-medium"
                >
                  View Details
                </button>
                
                {order.status === 'PENDING' && !isExpired(order.expiresAt) && (
                  <button
                    onClick={() => handleSignOrder(order.id)}
                    disabled={signing}
                    className="btn-primary text-sm px-4 py-2"
                  >
                    {signing ? 'Signing...' : 'Sign'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-text-primary">{selectedOrder.title}</h2>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-text-muted hover:text-text-primary"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Organization</p>
                  <p className="text-text-primary">{selectedOrder.organization.name}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Location</p>
                  <p className="text-text-primary">{selectedOrder.location.name}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-text-secondary mb-1">Content</p>
                  <p className="text-text-primary whitespace-pre-wrap">{selectedOrder.content}</p>
                </div>

                <div className="flex justify-between text-sm">
                  <div>
                    <p className="text-text-secondary">Created</p>
                    <p className="text-text-primary">{new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-text-secondary">Expires</p>
                    <p className="text-text-primary">{new Date(selectedOrder.expiresAt).toLocaleDateString()}</p>
                  </div>
                </div>

                {selectedOrder.status === 'SIGNED' && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-700">
                      Signed by {selectedOrder.signature} on {new Date(selectedOrder.signedAt!).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {selectedOrder.status === 'PENDING' && !isExpired(selectedOrder.expiresAt) && (
                  <button
                    onClick={() => handleSignOrder(selectedOrder.id)}
                    disabled={signing}
                    className="w-full btn-primary"
                  >
                    {signing ? 'Signing...' : 'Sign Post Order'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 